import { DAILY_REPORT_KINDS, type DailyReportKind } from '@felix/domain';
import {
  Button,
  Menu,
  Pagination,
  Select,
  Skeleton,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowRight,
  IconArrowsSort,
  IconChevronDown,
  IconEye,
  IconFileOff,
  IconPlus,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import classes from './Daily.module.css';
import {
  CARD_KINDS,
  type CardKind,
  filterRows,
  kindCards,
  noonBadge,
  pageOf,
  type ReportRow,
  type ReportSort,
  type ReportSortKey,
  reportBadge,
  reportRows,
  rowSummary,
  sortRows,
} from './logic.js';
import { ConfirmDialog, shipDateTime, useErrorText } from './parts.js';
import { type DetailAction, KIND_ICONS, NoonDetail, ReportDetail } from './ReportDetail.js';
import { ReportForm } from './ReportForm.js';
import type { DailyReportDetail, DailyReportsData, DailySource } from './types.js';

const PAGE_SIZE = 10;
const NOON_PATH = '/voyage/noon-report';

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: DailyReportsData };

type Panel =
  | { kind: 'none' }
  | { kind: 'report'; id: string }
  | { kind: 'noon'; id: string }
  | { kind: 'new'; report: DailyReportKind }
  | { kind: 'edit'; id: string };

/**
 * Operations ▸ Daily Reports (BGM dili; ortağın taslağı `ekran-1.jpeg`; ADR 0051 §9): tarih, tür ve
 * arama; tür kartları (Noon sefer modülünden, salt okunur); "Daily Reports List"; seçili raporun
 * paneli ya da "Daily Report Entry" formu. Gemide yazılır, kaptan onaylar; ofiste salt okunur.
 */
export function DailyReportsScreen({
  source,
  now = () => new Date(),
}: {
  source: DailySource;
  now?: () => Date;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.reports.${key}`, options ?? {});
  const shell = useShell();
  const errorText = useErrorText();
  const query = useMemo(() => new URLSearchParams(shell?.search ?? ''), [shell?.search]);
  const [range, setRange] = useState<{ from: string | null; to: string | null }>({
    from: query.get('from'),
    to: query.get('to'),
  });
  const [vesselId, setVesselId] = useState<string | null>(query.get('vessel'));
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [kindFilter, setKindFilter] = useState<DailyReportKind | 'noon' | 'all'>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ReportSort>({ key: 'at', dir: 'desc' });
  const [page, setPage] = useState(1);
  const editor = source.scope === 'ship' ? source.editor : undefined;
  const newKind = query.get('new');
  const [panel, setPanel] = useState<Panel>(() => {
    const id = query.get('report');
    if (id) return { kind: 'report', id };
    if (newKind && (DAILY_REPORT_KINDS as readonly string[]).includes(newKind)) {
      return { kind: 'new', report: newKind as DailyReportKind };
    }
    return { kind: 'none' };
  });
  const [detail, setDetail] = useState<DailyReportDetail | null>(null);
  const [dialog, setDialog] = useState<DetailAction | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const reload = useCallback(() => setReloads((n) => n + 1), []);
  // İlk yüklemede ve gün değişince en yeni rapor seçilir (ortağın taslağındaki gibi panel dolu);
  // kullanıcı paneli kapatınca kendiliğinden seçilmez
  const [autoPick, setAutoPick] = useState(true);
  const closePanel = () => {
    setAutoPick(false);
    setPanel({ kind: 'none' });
  };
  useEffect(() => {
    if (!autoPick || panel.kind !== 'none' || load.kind !== 'ready') return;
    const first = sortRows(
      reportRows(load.data.items, load.data.noon),
      { key: 'at', dir: 'desc' },
      load.data.names,
    )[0];
    if (first)
      setPanel(
        first.source === 'noon' ? { kind: 'noon', id: first.id } : { kind: 'report', id: first.id },
      );
  }, [autoPick, panel.kind, load]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: hata metni dil değişince yenilenmez; yükleme girdilere bağlı
  useEffect(() => {
    let alive = true;
    setLoad((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    source
      .reports({ vesselId, from: range.from, to: range.to })
      .then((data) => alive && setLoad({ kind: 'ready', data }))
      .catch(
        (failure: unknown) => alive && setLoad({ kind: 'error', message: errorText(failure) }),
      );
    return () => {
      alive = false;
    };
  }, [source, vesselId, range, reloads]);

  const detailId = panel.kind === 'report' || panel.kind === 'edit' ? panel.id : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: ayrıntı seçim ve yeniden yüklemeyle okunur
  useEffect(() => {
    let alive = true;
    if (!detailId) {
      setDetail(null);
      return;
    }
    source
      .report(detailId)
      .then((d) => alive && setDetail(d))
      .catch((failure: unknown) => {
        if (!alive) return;
        setNotice(errorText(failure));
        setPanel({ kind: 'none' });
      });
    return () => {
      alive = false;
    };
  }, [source, detailId, reloads]);

  const go = (path: string) => shell?.navigate(path);
  const header = (
    <PageHeader
      title={k('title')}
      titleHidden
      description={source.scope === 'ship' ? k('description') : k('descriptionOffice')}
      helpKey="operationsDailyReports"
    />
  );
  if (load.kind === 'loading') {
    return (
      <div className={classes.screen}>
        {header}
        <div className={classes.reportsGrid} aria-busy="true">
          <Skeleton height={100} radius="md" />
          <Skeleton height={420} radius="md" />
        </div>
      </div>
    );
  }
  if (load.kind === 'error') {
    return (
      <div className={classes.screen}>
        {header}
        <EmptyState
          tone="danger"
          title={k('loadError')}
          description={load.message}
          action={<Button onClick={reload}>{k('retry')}</Button>}
        />
      </div>
    );
  }
  const { data } = load;
  const rows = reportRows(data.items, data.noon);
  const visible = sortRows(
    filterRows(rows, { kind: kindFilter, query: search }, data.names),
    sort,
    data.names,
  );
  const paged = pageOf(visible, page, PAGE_SIZE);
  const single = data.from === data.to;
  const cards = kindCards(rows);
  const vesselName = data.vessel?.name ?? '';

  // Noon raporu kendi seferinde açılır (`?voyage=`, ADR 0047 §1; `?report=`, ADR 0051 §1)
  const openNoon = (noon: { id: string; voyageId: string }) =>
    go(
      `${NOON_PATH}?voyage=${noon.voyageId}&report=${noon.id}${source.scope === 'web' && data.vessel ? `&vessel=${data.vessel.id}` : ''}`,
    );
  const openRow = (row: ReportRow) =>
    setPanel(row.source === 'noon' ? { kind: 'noon', id: row.id } : { kind: 'report', id: row.id });
  const onSort = (key: ReportSortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'at' ? 'desc' : 'asc' },
    );

  const header2 = (key: ReportSortKey | 'actions', label: string) => {
    if (key === 'actions') {
      return (
        <th scope="col" data-center>
          {label}
        </th>
      );
    }
    const active = sort.key === key;
    const Icon = active
      ? sort.dir === 'asc'
        ? IconSortAscending
        : IconSortDescending
      : IconArrowsSort;
    return (
      <th
        scope="col"
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        <UnstyledButton
          className={classes.sortButton}
          data-active={active || undefined}
          onClick={() => onSort(key)}
          aria-label={k('sortBy', { column: label })}
        >
          {label}
          <Icon size={13} aria-hidden />
        </UnstyledButton>
      </th>
    );
  };

  const noonRow = panel.kind === 'noon' ? data.noon.find((n) => n.id === panel.id) : undefined;
  const side =
    editor && (panel.kind === 'new' || (panel.kind === 'edit' && detail)) ? (
      <ReportForm
        key={
          panel.kind === 'new' ? `new-${panel.report}` : `edit-${detail?.id}-${detail?.rowVersion}`
        }
        detail={panel.kind === 'edit' ? detail : null}
        kind={panel.kind === 'new' ? panel.report : (detail?.kind ?? 'daily')}
        vesselName={vesselName}
        tzOffsetMin={data.tzOffsetMin}
        nowUtc={now().toISOString()}
        editor={editor}
        onSaved={(id) => {
          setPanel({ kind: 'edit', id });
          reload();
        }}
        onSubmit={(id) => {
          setPanel({ kind: 'report', id });
          setDialog('submit');
          reload();
        }}
        onCancel={() => setPanel(detail ? { kind: 'report', id: detail.id } : { kind: 'none' })}
      />
    ) : panel.kind === 'report' && detail ? (
      <ReportDetail
        detail={detail}
        fileUrl={source.fileUrl}
        onClose={closePanel}
        onAction={(action) =>
          action === 'edit' ? setPanel({ kind: 'edit', id: detail.id }) : setDialog(action)
        }
        // Gözlemden arıza (ADR 0060 §7): gemide yeni arıza formu gözlemden dolar
        onConvert={
          editor && detail.kind === 'defect_observation'
            ? () => shell?.navigate(`/pms/defects?new=1&daily=${detail.id}`)
            : undefined
        }
        onOpenDefect={(id) => shell?.navigate(`/pms/defects?id=${id}`)}
      />
    ) : noonRow ? (
      <NoonDetail noon={noonRow} onClose={closePanel} onOpen={() => openNoon(noonRow)} />
    ) : panel.kind === 'none' ? (
      <section className={classes.card} data-panel>
        <p className={classes.muted}>{k('selectHint')}</p>
      </section>
    ) : (
      <Skeleton height={320} radius="md" />
    );

  return (
    <div className={classes.screen}>
      {header}
      <div className={classes.reportsToolbar}>
        <DayInput
          className={classes.toolDate}
          aria-label={t('daily.head.date')}
          value={data.to}
          onChange={(value) => {
            if (!value) return;
            setRange({ from: value, to: value });
            setPanel({ kind: 'none' });
            setAutoPick(true);
            setPage(1);
          }}
        />
        {!single ? (
          <UnstyledButton
            className={classes.rangeChip}
            onClick={() => setRange({ from: data.to, to: data.to })}
          >
            {formatDay(data.from)} – {formatDay(data.to)}
            <IconX size={14} aria-hidden />
          </UnstyledButton>
        ) : null}
        {data.vessels && data.vessels.length > 1 ? (
          <Select
            className={classes.toolSelect}
            aria-label={t('daily.head.vessel')}
            data={data.vessels.map((v) => ({ value: v.id, label: v.name }))}
            value={data.vessel?.id ?? null}
            onChange={(v) => v && setVesselId(v)}
            allowDeselect={false}
          />
        ) : null}
        <Select
          className={classes.toolSelect}
          aria-label={k('typeFilter')}
          data={[
            { value: 'all', label: k('allTypes') },
            ...CARD_KINDS.map((v) => ({ value: v, label: t(`daily.kinds.${v}`) })),
          ]}
          value={kindFilter}
          onChange={(v) => {
            setKindFilter((v ?? 'all') as typeof kindFilter);
            setPage(1);
          }}
          allowDeselect={false}
        />
        <TextInput
          className={classes.search}
          leftSection={<IconSearch size={16} aria-hidden />}
          placeholder={k('search')}
          aria-label={k('searchLabel')}
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            setPage(1);
          }}
        />
        {editor && data.canCreate ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                leftSection={<IconPlus size={16} />}
                rightSection={<IconChevronDown size={14} />}
              >
                {k('newReport')}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {DAILY_REPORT_KINDS.map((kind) => (
                <Menu.Item key={kind} onClick={() => setPanel({ kind: 'new', report: kind })}>
                  {t(`daily.kinds.${kind}`)}
                </Menu.Item>
              ))}
              <Menu.Divider />
              <Menu.Item onClick={() => go(NOON_PATH)}>{k('newNoon')}</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </div>
      {notice ? (
        <p className={classes.error} role="alert">
          {notice}
          <UnstyledButton
            className={classes.iconButton}
            onClick={() => setNotice(null)}
            aria-label={t('daily.details.close')}
          >
            <IconX size={14} />
          </UnstyledButton>
        </p>
      ) : null}
      <ul className={classes.kindCards}>
        {cards.map((card) => (
          <li key={card.kind}>
            <KindCard
              kind={card.kind}
              latest={card.latest}
              openItems={card.openItems}
              onOpen={() => (card.latest ? openRow(card.latest) : setKindFilter(card.kind))}
            />
          </li>
        ))}
      </ul>
      <div className={classes.reportsGrid}>
        <section className={classes.card} aria-labelledby="daily-list-title">
          <h2 id="daily-list-title" className={classes.cardTitle}>
            {k('listTitle')}
          </h2>
          {rows.length === 0 ? (
            <EmptyState
              icon={<IconFileOff size={28} />}
              title={k('emptyTitle')}
              description={editor ? k('emptyText') : k('emptyOffice')}
            />
          ) : visible.length === 0 ? (
            <p className={classes.muted}>{k('noMatch')}</p>
          ) : (
            <>
              <div className={classes.tableWrap}>
                <table className={classes.table} data-variant="reports">
                  <thead>
                    <tr>
                      {header2('at', k('columns.dateTime'))}
                      {header2('kind', k('columns.type'))}
                      {header2('summary', k('columns.summary'))}
                      {header2('status', k('columns.status'))}
                      {header2('submittedBy', k('columns.submittedBy'))}
                      {header2('actions', k('columns.actions'))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.rows.map((row) => {
                      const badge =
                        row.source === 'daily' ? reportBadge(row.item) : noonBadge(row.noon.status);
                      const by =
                        row.source === 'daily'
                          ? row.item.submittedBy
                            ? (data.names[row.item.submittedBy] ?? '—')
                            : '—'
                          : (row.noon.submittedBy ?? '—');
                      const selected =
                        (panel.kind === 'report' ||
                          panel.kind === 'edit' ||
                          panel.kind === 'noon') &&
                        panel.id === row.id;
                      return (
                        <tr key={row.id} data-selected={selected || undefined}>
                          <td className={classes.nowrap}>{shipDateTime(row.at, row.offset)}</td>
                          <td>
                            <span className={classes.kindLabel} data-kind={row.kind}>
                              <span className={classes.kindIconSmall} aria-hidden>
                                {KIND_ICONS[row.kind]}
                              </span>
                              {t(`daily.kinds.${row.kind}`)}
                            </span>
                          </td>
                          <td className={classes.summaryCell}>
                            {row.source === 'noon' ? `${row.noon.voyageNo} · ` : ''}
                            {rowSummary(row) || '—'}
                          </td>
                          <td>
                            <StatusBadge tone={badge.tone} withIcon={false}>
                              {t(`daily.reportStatuses.${badge.key}`)}
                            </StatusBadge>
                          </td>
                          <td className={classes.wrapCell}>{by}</td>
                          <td data-center>
                            <UnstyledButton
                              className={classes.iconButton}
                              onClick={() => openRow(row)}
                              aria-label={k('view', { report: t(`daily.kinds.${row.kind}`) })}
                            >
                              <IconEye size={18} />
                            </UnstyledButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {paged.pages > 1 ? (
                <Pagination
                  className={classes.pager}
                  size="sm"
                  total={paged.pages}
                  value={paged.page}
                  onChange={setPage}
                  aria-label={k('pagination')}
                  getControlProps={(control) => ({ 'aria-label': k(`pager.${control}`) })}
                />
              ) : null}
            </>
          )}
        </section>
        <div className={classes.reportsSide}>{side}</div>
      </div>
      {detail && editor && dialog ? (
        <DetailDialog
          detail={detail}
          action={dialog}
          onClose={() => setDialog(null)}
          onDone={(next) => {
            setDialog(null);
            if (next === null) setPanel({ kind: 'none' });
            else if (next.status === 'returned' && dialog === 'reopen')
              setPanel({ kind: 'edit', id: next.id });
            reload();
          }}
          editorOf={editor}
        />
      ) : null}
    </div>
  );
}

function KindCard({
  kind,
  latest,
  openItems,
  onOpen,
}: {
  kind: CardKind;
  latest: ReportRow | null;
  openItems: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.reports.card.${key}`, options ?? {});
  const badge = latest
    ? latest.source === 'daily'
      ? reportBadge(latest.item)
      : noonBadge(latest.noon.status)
    : null;
  return (
    <UnstyledButton
      className={classes.kindCard}
      data-kind={kind}
      onClick={onOpen}
      aria-label={k('open', { kind: t(`daily.kinds.${kind}`) })}
    >
      <span className={classes.kindIcon} data-kind={kind} aria-hidden>
        {KIND_ICONS[kind]}
      </span>
      <span className={classes.kindBody}>
        <strong>{t(`daily.kinds.${kind}`)}</strong>
        {badge ? (
          <span className={classes.kindStatus} data-tone={badge.tone}>
            {t(`daily.reportStatuses.${badge.key}`)}
          </span>
        ) : (
          <span className={classes.kindStatus}>{kind === 'noon' ? k('noVoyage') : k('none')}</span>
        )}
        <small>
          {kind === 'defect_observation' && openItems > 0
            ? k('openItems', { count: openItems })
            : latest
              ? shipDateTime(latest.at, latest.offset)
              : ''}
        </small>
      </span>
      <IconArrowRight size={16} className={classes.kindArrow} aria-hidden />
    </UnstyledButton>
  );
}

/** Gönderim, onay, iade, yeniden açma ve taslaktan vazgeçme pencereleri (ADR 0051 §6). */
function DetailDialog({
  detail,
  action,
  onClose,
  onDone,
  editorOf,
}: {
  detail: DailyReportDetail;
  action: DetailAction;
  onClose: () => void;
  onDone: (next: DailyReportDetail | null) => void;
  editorOf: NonNullable<DailySource['editor']>;
}) {
  const { t } = useTranslation();
  const d = (key: string, options?: Record<string, unknown>) =>
    t(`daily.dialogs.${key}`, options ?? {});
  const expected = () => ({
    submissionId: detail.pending?.submissionId ?? '',
    step: detail.pending?.step ?? 1,
    contentHash: detail.contentHash ?? '',
  });
  switch (action) {
    case 'submit':
      return (
        <ConfirmDialog
          opened
          title={d('submitTitle')}
          text={d('submitText')}
          reasonLabel={detail.revision > 0 ? d('submitReason') : undefined}
          reasonRequired={detail.revision > 0}
          confirmLabel={t('daily.detail.submit')}
          onClose={onClose}
          onConfirm={async ({ reason }) =>
            onDone(await editorOf.submitReport(detail.id, reason === '' ? null : reason))
          }
        />
      );
    case 'approve':
      return (
        <ConfirmDialog
          opened
          title={d('approveTitle')}
          text={d('approveText', {
            kind: t(`daily.kinds.${detail.kind}`),
            day: formatDay(detail.reportDay),
          })}
          password
          confirmLabel={t('daily.detail.approve')}
          onClose={onClose}
          onConfirm={async ({ password }) =>
            onDone(
              await editorOf.decide(detail.id, {
                ...(password ? { password } : {}),
                decision: 'approved',
                expected: expected(),
              }),
            )
          }
        />
      );
    case 'return':
      return (
        <ConfirmDialog
          opened
          title={d('returnTitle')}
          text={d('returnText')}
          reasonLabel={d('reason')}
          reasonRequired
          password
          danger
          confirmLabel={t('daily.detail.return')}
          onClose={onClose}
          onConfirm={async ({ password, reason }) =>
            onDone(
              await editorOf.decide(detail.id, {
                ...(password ? { password } : {}),
                decision: 'returned',
                reason,
                expected: expected(),
              }),
            )
          }
        />
      );
    case 'reopen':
      return (
        <ConfirmDialog
          opened
          title={d('reopenTitle')}
          text={d('reopenText')}
          reasonLabel={d('reason')}
          reasonRequired
          password
          confirmLabel={d('confirm')}
          onClose={onClose}
          onConfirm={async ({ password, reason }) =>
            onDone(
              await editorOf.reopen(detail.id, {
                ...(password ? { password } : {}),
                reason,
                contentHash: detail.contentHash ?? '',
              }),
            )
          }
        />
      );
    case 'discard':
      return (
        <ConfirmDialog
          opened
          title={d('discardTitle')}
          text={d('discardText')}
          reasonLabel={d('reason')}
          reasonRequired
          danger
          confirmLabel={t('daily.detail.discard')}
          onClose={onClose}
          onConfirm={async ({ reason }) => {
            await editorOf.discardReport(detail.id, detail.rowVersion, reason);
            onDone(null);
          }}
        />
      );
    default:
      return null;
  }
}
